/**
 * What the client reads and does on the "sign your declarations" step of onboarding: the plain
 * words for what is still missing from their answers, what they add while signing, and when the
 * signing screen lets them sign. Pure functions, so the rules are tested without a screen.
 *
 * Client vocabulary: never "fund", "portfolio" or "advisor"; "series", "holdings", "Account
 * Executive". The sealed words of each statement come from the server and are shown as they are.
 */
import type {
  ExtraInput,
  ReasonInput,
  StatementKind,
  StatementsPreview,
  TypedInput,
} from '../services/kycStatements.service';

/** The answers of the profile a statement cannot be written without, in plain words. Each is shown
 *  as a line of its own, so the Spanish screen translates it whole. */
export const MISSING_LABELS: Record<string, string> = {
  name: 'Your name',
  'country of residence': 'Your country of residence',
  nationality: 'Your nationality',
  'date of birth': 'Your date of birth',
  'country of birth': 'Your country of birth',
  address: 'Your address',
  'account holder': 'Your answer about acting on your own account',
  'public positions': 'Your answer about public positions',
  'source of the money': 'The source of the money',
  'lawful origin of the money': 'Your declaration about the origin of the money',
};

export function missingThings(missing: Record<StatementKind, string[]>): string[] {
  const all = [...missing.crs_self_certification, ...missing.kyc_declarations];
  return [...new Set(all.map((m) => MISSING_LABELS[m]).filter((m): m is string => !!m))];
}

export const EMPTY_EXTRA: ExtraInput = { country: '', tin: '', reason: '', explanation: '' };

/** The reason a country needs needs a few words when the person cannot obtain the number. */
export const NEEDS_EXPLANATION = 'unable_to_obtain';

export interface SigningEntries {
  reasons: Record<string, ReasonInput>;
  extras: ExtraInput[];
  signerName: string;
  confirmed: boolean;
  mfaCode: string;
}

export function typedInput(language: 'en' | 'es', entries: SigningEntries): TypedInput {
  return {
    language,
    noTinReasons: entries.reasons,
    extras: entries.extras.filter((e) => e.country !== ''),
  };
}

/** True when the signing screen lets the person sign. The server decides again. */
export function canSign(preview: StatementsPreview, entries: SigningEntries): boolean {
  if (!preview.applies || !preview.editable || preview.acts_for_another) return false;
  const missing = missingThings(preview.missing);
  if (missing.length > 0 || preview.error !== null) return false;
  if (!entries.confirmed || entries.signerName.trim().length < 2) return false;
  return !preview.step_up_required || /^\d{6}$/.test(entries.mfaCode);
}

/** True when both statements are current, so there is nothing to sign. */
export function allSigned(preview: StatementsPreview): boolean {
  return Object.values(preview.status).every((state) => state === 'current');
}
